import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import { FaCartShopping, FaMagnifyingGlass, FaRegSquare, FaSquareCheck, FaTrash } from "react-icons/fa6";
import { Link, useSearchParams } from "react-router-dom";
import ListingPage from '../../components/DynamicListingPage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteOrder, getOrders } from '../../helpers/http_orders_requests';
import { getCategories } from '../../helpers/http_categories_requests';
import { useAuth } from "../../providers/AuthProvider";
import { usePermissions } from "../../providers/PermissionsProvider";
import { confirmAndExecuteFn } from '../../utils/functions';
import { useState } from "react";
import { useCatalog } from "../../providers/CatalogProvider";
import { getCustomers } from "../../helpers/http_customers_requests";

export default function Orders() {

    const { verifyPermission } = usePermissions();
    const { metalList } = useCatalog();

    const [searchParams] = useSearchParams();
    const [filterValues, setFilterValues] = useState(() => (
        searchParams.get("overdue") === "1" ? { overdue: "1" } : {}
    ));
    const handleFilterChange = (field, value) => {
        setFilterValues(prev => ({ ...prev, [field]: value }));
    };

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 60 });
    const categories = categoriesQuery?.data?.data?.data?.map(item => ({ value: item.id, label: item.name })) || [];

    const { data, isFetching, refetch } = useQuery({
        queryKey: ['getOrders', filterValues],
        queryFn: () => getOrders(filterValues),
        staleTime: 1000 * 5
    });

    const { data: customersData } = useQuery({
        queryKey: ["customers"],
        queryFn: getCustomers,
        staleTime: 1000 * 60 * 10,
    });

    const { customers } = customersData?.data ?? [];


    const { data: dataList } = data?.data ?? {};

    const { id: user_id } = useAuth();

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteOrder(id, user_id),
        onSuccess: () => {
            setTimeout(() => {
                refetch();
            }, 1000);
        }
    });

    const handleDelete = (id) => {
        confirmAndExecuteFn('Tem certeza que deseja deletar este pedido?', () => {
            deleteMutation.mutate(id);
        });
    };

    const columns = [
        { field: 'id', headerName: 'ID' },
        { field: 'image', headerName: 'Foto' },
        {
            field: 'category_name',
            headerName: 'Categoria',
        },
        {
            field: 'created_by',
            headerName: 'Cadastrado',
        },
        {
            field: 'customer_name',
            headerName: 'Cliente',
        },
        {
            field: 'selected_metal',
            headerName: 'Material',
        },
        {
            field: 'current_step',
            headerName: 'Status',
            sortable: true,
        },
        {
            field: 'ordered_at',
            headerName: 'Data de Pedido',
        },
        {
            field: 'delivery_date',
            headerName: 'Data de Entrega',
        },
        {
            field: 'price',
            headerName: 'Preço',
        },
        {
            field: 'actions',
            headerName: 'Ações',
        },
    ];

    const filters = [
        { field: 'category', type: 'select', label: 'Categoria', options: categories },
        {
            field: "customer",
            type: "select",
            label: "Cliente",
            options: customers?.map((cust) => ({
                value: cust.id,
                label: cust.complete_name,
            })),
        },
        { field: 'selected_metal', type: 'select', label: 'Material', options: metalList },
        { field: 'ordered_at_start', type: 'datepicker', label: 'Data de Pedido Início' },
        { field: 'ordered_at_end', type: 'datepicker', label: 'Data de Pedido Fim' },
        { field: 'delivery_date_start', type: 'datepicker', label: 'Data de Entrega Início' },
        { field: 'delivery_date_end', type: 'datepicker', label: 'Data de Entrega Fim' },
        {
            field: 'order_status',
            type: 'select',
            label: 'Status',
            options: [
                { value: 'open', label: 'Em aberto' },
                { value: 'ready', label: 'Pronto' },
                { value: 'delivered', label: 'Entregue' },
            ],
        },
        {
            field: 'overdue',
            type: 'select',
            label: 'Prazo',
            options: [
                { value: '1', label: 'Atrasados' },
                { value: '0', label: 'No prazo' },
            ],
        },
    ];

    const componentName = 'orders';

    const currentStep = ({ current_step }) => {
        const step = Number(current_step);

        const config = {
            variant: step === 9 ? "contained" : "outlined",
            label:
                step === 0 ? "Triagem" :
                    step < 8 ? "Produção" :
                        step === 8 ? "Pronto" : "Entregue",
            color:
                step === 0 ? "info" :
                    step < 8 ? "warning" :
                        step === 8 ? "secondary" : "success",
        };

        return <Chip {...config} />;
    };

    const renderActions = (row) => {
        const verificationAction = {
            '0': verifyPermission(componentName, 2) && (
                <Tooltip title="Verificação" arrow placement="top">
                    <IconButton component={Link} to={`/pedido-verificacao/${row.id}`} color="primary">
                        <FaRegSquare size={20} />
                    </IconButton>
                </Tooltip>
            ),
            '1': (
                <Tooltip title="Verificado" arrow placement="top">
                    <Box component="span" sx={{ display: 'inline-flex', color: 'success.main' }}>
                        <FaSquareCheck size={20} />
                    </Box>
                </Tooltip>
            ),
        }[row.status];

        return (
            <Stack spacing={1} direction={'row'} alignItems={'center'} justifyContent={'center'}>
                {verificationAction}
                <Tooltip title="Detalhes" arrow placement="top">
                    <IconButton component={Link} to={`/pedido-detalhes/${row.id}`} color="primary">
                        <FaMagnifyingGlass size={20} />
                    </IconButton>
                </Tooltip>
                {verifyPermission(componentName, 4) && (
                    <Tooltip title="Deletar" arrow placement="top">
                        <IconButton color="error" onClick={() => handleDelete(row.id)}>
                            <FaTrash size={20} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        );
    };

    return <ListingPage
        filters={filters}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        currentStep={currentStep}
        isLoading={isFetching}
        icon={FaCartShopping}
        pageTitle={"Pedidos"}
        addButtonTitle={"Pedido"}
        addPath={"/novo-pedido"}
        columns={columns}
        data={dataList}
        actions={renderActions}
        getRowHref={(row) => `/pedido-detalhes/${row.id}`}
    />
};